import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import {
  documentAttestationDefaultValues,
  type DocumentAttestationFormValues,
} from '../schemas';
import type { RequiredDocument } from '../types/activities.types';

export type SubmitHandlerFn = () => Promise<boolean> | boolean;

interface DocumentAttestationState extends BaseStoreState {
  formData: Partial<DocumentAttestationFormValues>;

  isValid: boolean;

  submitHandler: SubmitHandlerFn | null;

  uploadedFiles: Record<string, any[]>;
  pendingApiError: { field: string; message: string } | null;
  requiredDocuments: RequiredDocument[];
  rejectionReason: string | null;
}

export interface DocumentAttestationStore extends DocumentAttestationState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<DocumentAttestationFormValues>) => void;
  setFieldError: (_field: string, _error: string | null) => void;
  clearApiError: () => void;
  setSubmitHandler: (_fn: SubmitHandlerFn | null) => void;
  setUploadedFiles: (_files: Record<string, any[]>) => void;
  setRequiredDocuments: (_docs: RequiredDocument[]) => void;
  setRejectionReason: (_reason: string | null) => void;
  resetForm: () => void;
  getApiPayload: () => Record<string, unknown>;
}

const initialFormData =
  documentAttestationDefaultValues as Partial<DocumentAttestationFormValues>;

export const useDocumentAttestationStore =
  createPersistedStore<DocumentAttestationState>(
    'document-attestation',
    {
      formData: initialFormData,

      isValid: false,

      submitHandler: null,

      uploadedFiles: {},
      pendingApiError: null,
      requiredDocuments: [],
      rejectionReason: null,
    },
    (set, get) => ({
      setValidity: (isValid: boolean) => set({ isValid }),

      setFormData: (data: Partial<DocumentAttestationFormValues>) =>
        set((state: DocumentAttestationState) => ({
          formData: { ...state.formData, ...data },
        })),

      setFieldError: (field: string, error: string | null) =>
        set({ pendingApiError: error ? { field, message: error } : null }),

      clearApiError: () => set({ pendingApiError: null }),

      setSubmitHandler: (fn: SubmitHandlerFn | null) =>
        set({ submitHandler: fn }),

      setUploadedFiles: (files: Record<string, any[]>) =>
        set({ uploadedFiles: files }),

      setRequiredDocuments: (docs: RequiredDocument[]) =>
        set({ requiredDocuments: docs }),

      setRejectionReason: (reason: string | null) =>
        set({ rejectionReason: reason }),

      resetForm: () =>
        set({
          formData: initialFormData,
          isValid: false,

          submitHandler: null,

          uploadedFiles: {},
          pendingApiError: null,
          requiredDocuments: [],
          rejectionReason: null,
        }),

      getApiPayload: () => {
        const { formData } = get();

        return { ...formData };
      },
    }),
    {
      partialize: (state: DocumentAttestationState) => ({
        formData: state.formData,
        isValid: state.isValid,

        uploadedFiles: state.uploadedFiles,
        requiredDocuments: state.requiredDocuments,
        rejectionReason: state.rejectionReason,
      }),
      clearOnRefresh: ['uploadedFiles'],
    }
  ) as unknown as PersistedStoreHook<DocumentAttestationStore>;

// Selectors — interfaces with DocumentAttestationLayout unchanged
export const useDocumentAttestationFormData = () =>
  useDocumentAttestationStore(
    (state: DocumentAttestationState) => state.formData
  );
export const useDocumentAttestationErrors = () =>
  ({}) as Partial<Record<keyof DocumentAttestationFormValues, string>>;
export const useDocumentAttestationIsValid = () =>
  useDocumentAttestationStore(
    (state: DocumentAttestationState) => state.isValid
  );
export const useDocumentAttestationUploadedFiles = () =>
  useDocumentAttestationStore(
    (state: DocumentAttestationState) => state.uploadedFiles
  );
export const useDocumentAttestationApiPayload = () =>
  useDocumentAttestationStore(
    (state: DocumentAttestationState) =>
      (state as unknown as DocumentAttestationStore).getApiPayload
  );
